import type { Platform } from '@/types';

interface DeeplinkResult {
  deepLinkUrl: string;
  fallbackUrl: string;
}

// Extract ID patterns for various platforms
const extractors: Record<string, (url: string) => string | null> = {
  spotify: (url: string) => {
    // Handle: open.spotify.com/track/ID, open.spotify.com/intl-fr/track/ID, etc.
    const match = url.match(/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?(track|album|playlist|artist|show|episode)\/([a-zA-Z0-9]+)/);
    if (match) return `${match[1]}/${match[2]}`;
    return null;
  },

  youtube: (url: string) => {
    // Handle: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/shorts/ID
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]+)/);
    if (match) return match[1];
    return null;
  },

  tiktok: (url: string) => {
    // Handle: tiktok.com/@user/video/ID
    const match = url.match(/tiktok\.com\/@([^/]+)\/video\/(\d+)/);
    if (match) return `${match[1]}/${match[2]}`;
    // Handle: vm.tiktok.com/ID
    const shortMatch = url.match(/vm\.tiktok\.com\/([a-zA-Z0-9]+)/);
    if (shortMatch) return shortMatch[1];
    return null;
  },

  instagram: (url: string) => {
    // Handle: instagram.com/p/ID, instagram.com/reel/ID, instagram.com/username
    const postMatch = url.match(/instagram\.com\/(p|reel|tv)\/([a-zA-Z0-9_-]+)/);
    if (postMatch) return `media/${postMatch[2]}`;
    const userMatch = url.match(/instagram\.com\/([a-zA-Z0-9_.]+)/);
    if (userMatch && !['p', 'reel', 'tv', 'explore', 'direct'].includes(userMatch[1])) {
      return `user/${userMatch[1]}`;
    }
    return null;
  },

  amazon: (url: string) => {
    // Handle: amazon.com/dp/ASIN, amazon.com/gp/product/ASIN
    const match = url.match(/amazon\.[a-z.]+\/(?:dp|gp\/product)\/([A-Z0-9]+)/i);
    if (match) return match[1];
    return null;
  },

  twitter: (url: string) => {
    // Handle: twitter.com/user/status/ID, x.com/user/status/ID
    const statusMatch = url.match(/(?:twitter|x)\.com\/([^/]+)\/status\/(\d+)/);
    if (statusMatch) return `status/${statusMatch[2]}`;
    const userMatch = url.match(/(?:twitter|x)\.com\/([a-zA-Z0-9_]+)/);
    if (userMatch && !['home', 'explore', 'search', 'notifications', 'messages'].includes(userMatch[1])) {
      return `user/${userMatch[1]}`;
    }
    return null;
  },

  facebook: (url: string) => {
    // Handle various Facebook URL formats
    const match = url.match(/facebook\.com\/([^/?]+)/);
    if (match) return match[1];
    return null;
  },

  deezer: (url: string) => {
    // Handle: deezer.com/track/ID, deezer.com/fr/track/ID, deezer.com/album/ID, etc.
    const match = url.match(/deezer\.com\/(?:[a-z]{2}\/)?(track|album|playlist|artist)\/(\d+)/);
    if (match) return `${match[1]}/${match[2]}`;
    return null;
  },

  ebay: (url: string) => {
    // Handle: ebay.com/itm/ID, ebay.fr/itm/ID
    const match = url.match(/ebay\.[a-z.]+\/itm\/(?:[^/]*\/)?(\d+)/);
    if (match) return match[1];
    return null;
  },

  etsy: (url: string) => {
    // Handle: etsy.com/listing/ID
    const match = url.match(/etsy\.com\/listing\/(\d+)/);
    if (match) return match[1];
    return null;
  },

  soundcloud: (url: string) => {
    // Handle: soundcloud.com/artist/track
    const match = url.match(/soundcloud\.com\/([^/]+\/[^/?]+)/);
    if (match) return match[1];
    return null;
  },

  snapchat: (url: string) => {
    // Handle: snapchat.com/add/USERNAME, t.snapchat.com/USERNAME
    const addMatch = url.match(/snapchat\.com\/add\/([a-zA-Z0-9._-]+)/);
    if (addMatch) return addMatch[1];
    return null;
  },

  pinterest: (url: string) => {
    // Handle: pinterest.com/pin/ID or pinterest.com/USERNAME
    const pinMatch = url.match(/pinterest\.com\/pin\/(\d+)/);
    if (pinMatch) return `pin/${pinMatch[1]}`;
    const userMatch = url.match(/pinterest\.com\/([a-zA-Z0-9_]+)\/?$/);
    if (userMatch) return `user/${userMatch[1]}`;
    return null;
  },

  reddit: (url: string) => {
    // Handle: reddit.com/r/SUBREDDIT/comments/ID, reddit.com/r/SUBREDDIT
    const match = url.match(/reddit\.com\/(r\/[^\s?]+)/);
    if (match) return match[1];
    return null;
  },

  weibo: (url: string) => {
    // Handle: weibo.com/ID or m.weibo.cn/detail/ID
    const detailMatch = url.match(/weibo\.c(?:om|n)\/(?:detail\/)?(\d+)/);
    if (detailMatch) return detailMatch[1];
    return null;
  },

  vimeo: (url: string) => {
    // Handle: vimeo.com/ID
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (match) return match[1];
    return null;
  },

  telegram: (url: string) => {
    // Handle: t.me/USERNAME or t.me/s/USERNAME
    const match = url.match(/(?:t\.me|telegram\.me)\/(?:s\/)?([a-zA-Z0-9_]+)/);
    if (match) return match[1];
    return null;
  },

  twitch: (url: string) => {
    // Handle: twitch.tv/CHANNEL
    const match = url.match(/twitch\.tv\/([a-zA-Z0-9_]+)/);
    if (match && !['directory', 'videos', 'settings'].includes(match[1])) return match[1];
    return null;
  },

  tidal: (url: string) => {
    // Handle: tidal.com/browse/track/ID, listen.tidal.com/track/ID
    const match = url.match(/tidal\.com\/(?:browse\/)?(track|album|playlist|artist)\/([a-zA-Z0-9-]+)/);
    if (match) return `${match[1]}/${match[2]}`;
    return null;
  },

  kick: (url: string) => {
    // Handle: kick.com/CHANNEL
    const match = url.match(/kick\.com\/([a-zA-Z0-9_]+)/);
    if (match) return match[1];
    return null;
  },

  audiomack: (url: string) => {
    // Handle: audiomack.com/artist/song/ID
    const match = url.match(/audiomack\.com\/([^/?]+(?:\/[^/?]+)*)/);
    if (match) return match[1];
    return null;
  },

  mixcloud: (url: string) => {
    // Handle: mixcloud.com/user/mix-name
    const match = url.match(/mixcloud\.com\/([^/?]+(?:\/[^/?]+)?)/);
    if (match) return match[1];
    return null;
  },

  tunein: (url: string) => {
    // Handle: tunein.com/radio/station-ID
    const match = url.match(/tunein\.com\/(radio|podcast)\/[^/]+-([a-zA-Z0-9]+)/);
    if (match) return `${match[1]}/${match[2]}`;
    return null;
  },

  threads: (url: string) => {
    // Handle: threads.net/@username or threads.net/@username/post/ID
    const postMatch = url.match(/threads\.net\/@([^/]+)\/post\/([a-zA-Z0-9_-]+)/);
    if (postMatch) return `post/${postMatch[2]}`;
    const userMatch = url.match(/threads\.net\/@([a-zA-Z0-9_.]+)/);
    if (userMatch) return `user/${userMatch[1]}`;
    return null;
  },

  strava: (url: string) => {
    // Handle: strava.com/athletes/ID, strava.com/activities/ID, strava.com/clubs/NAME
    const match = url.match(/strava\.com\/(athletes|activities|clubs)\/([a-zA-Z0-9_-]+)/);
    if (match) return `${match[1]}/${match[2]}`;
    return null;
  },

  airbnb: (url: string) => {
    // Handle: airbnb.com/rooms/ID
    const match = url.match(/airbnb\.[a-z.]+\/rooms\/(\d+)/);
    if (match) return match[1];
    return null;
  },

  imgur: (url: string) => {
    // Handle: imgur.com/a/ID (album) or imgur.com/ID (image)
    const albumMatch = url.match(/imgur\.com\/a\/([a-zA-Z0-9]+)/);
    if (albumMatch) return `a/${albumMatch[1]}`;
    const imageMatch = url.match(/imgur\.com\/([a-zA-Z0-9]+)/);
    if (imageMatch && !['a', 'gallery', 'r', 'user', 't'].includes(imageMatch[1])) return imageMatch[1];
    return null;
  },

  substack: (url: string) => {
    // Handle: username.substack.com/p/post-slug or substack.com/@username
    const postMatch = url.match(/([^.]+)\.substack\.com\/p\/([^/?]+)/);
    if (postMatch) return `${postMatch[1]}/p/${postMatch[2]}`;
    const userMatch = url.match(/substack\.com\/@([a-zA-Z0-9_]+)/);
    if (userMatch) return `@${userMatch[1]}`;
    return null;
  },

  behance: (url: string) => {
    // Handle: behance.net/gallery/ID/name or behance.net/username
    const galleryMatch = url.match(/behance\.net\/gallery\/(\d+)/);
    if (galleryMatch) return `gallery/${galleryMatch[1]}`;
    const userMatch = url.match(/behance\.net\/([a-zA-Z0-9_]+)/);
    if (userMatch && !['gallery', 'search', 'joblist'].includes(userMatch[1])) return `user/${userMatch[1]}`;
    return null;
  },

  kickstarter: (url: string) => {
    // Handle: kickstarter.com/projects/creator/project-name
    const match = url.match(/kickstarter\.com\/projects\/([^/]+\/[^/?]+)/);
    if (match) return match[1];
    return null;
  },

  bandsintown: (url: string) => {
    // Handle: bandsintown.com/a/ID or bandsintown.com/artist/NAME
    const match = url.match(/bandsintown\.com\/(?:a|artist)\/([^/?]+)/);
    if (match) return match[1];
    return null;
  },
};

// Generate deeplink URL based on platform
const deeplinkGenerators: Record<string, (id: string, originalUrl: string) => DeeplinkResult> = {
  spotify: (id: string, originalUrl: string) => ({
    deepLinkUrl: `spotify://${id}`,
    fallbackUrl: originalUrl,
  }),

  'apple-music': (_id: string, originalUrl: string) => ({
    deepLinkUrl: originalUrl.replace('https://', 'music://'),
    fallbackUrl: originalUrl,
  }),

  youtube: (id: string, originalUrl: string) => ({
    deepLinkUrl: `vnd.youtube://${id}`,
    fallbackUrl: originalUrl,
  }),

  'youtube-music': (_id: string, originalUrl: string) => ({
    deepLinkUrl: `youtubemusic://watch?${originalUrl.split('?')[1] || ''}`,
    fallbackUrl: originalUrl,
  }),

  tiktok: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `snssdk1233://`,
    fallbackUrl: originalUrl,
  }),

  instagram: (id: string, originalUrl: string) => {
    if (id.startsWith('media/')) {
      return {
        deepLinkUrl: `instagram://media?id=${id.replace('media/', '')}`,
        fallbackUrl: originalUrl,
      };
    }
    if (id.startsWith('user/')) {
      return {
        deepLinkUrl: `instagram://user?username=${id.replace('user/', '')}`,
        fallbackUrl: originalUrl,
      };
    }
    return { deepLinkUrl: 'instagram://', fallbackUrl: originalUrl };
  },

  amazon: (id: string, originalUrl: string) => ({
    deepLinkUrl: `com.amazon.mobile.shopping://www.amazon.com/dp/${id}`,
    fallbackUrl: originalUrl,
  }),

  twitter: (id: string, originalUrl: string) => {
    if (id.startsWith('status/')) {
      return {
        deepLinkUrl: `twitter://status?id=${id.replace('status/', '')}`,
        fallbackUrl: originalUrl,
      };
    }
    if (id.startsWith('user/')) {
      return {
        deepLinkUrl: `twitter://user?screen_name=${id.replace('user/', '')}`,
        fallbackUrl: originalUrl,
      };
    }
    return { deepLinkUrl: 'twitter://', fallbackUrl: originalUrl };
  },

  facebook: (id: string, originalUrl: string) => ({
    deepLinkUrl: `fb://page/${id}`,
    fallbackUrl: originalUrl,
  }),

  whatsapp: (_id: string, originalUrl: string) => {
    const phoneMatch = originalUrl.match(/wa\.me\/(\d+)/);
    if (phoneMatch) {
      return {
        deepLinkUrl: `whatsapp://send?phone=${phoneMatch[1]}`,
        fallbackUrl: originalUrl,
      };
    }
    return { deepLinkUrl: 'whatsapp://', fallbackUrl: originalUrl };
  },

  deezer: (id: string, originalUrl: string) => ({
    deepLinkUrl: `deezer://www.deezer.com/${id}`,
    fallbackUrl: originalUrl,
  }),

  ebay: (id: string, originalUrl: string) => ({
    deepLinkUrl: `ebay://launch?itm=${id}`,
    fallbackUrl: originalUrl,
  }),

  'google-maps': (_id: string, originalUrl: string) => ({
    deepLinkUrl: `comgooglemaps://?${originalUrl.split('?')[1] || ''}`,
    fallbackUrl: originalUrl,
  }),

  linkedin: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `linkedin://${originalUrl.replace('https://www.linkedin.com/', '')}`,
    fallbackUrl: originalUrl,
  }),

  notion: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `notion://${originalUrl.replace(/https?:\/\//, '')}`,
    fallbackUrl: originalUrl,
  }),

  slack: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `slack://${originalUrl.replace(/https?:\/\//, '')}`,
    fallbackUrl: originalUrl,
  }),

  // Existing platforms — add missing deeplink generators
  soundcloud: (id: string, originalUrl: string) => ({
    deepLinkUrl: `soundcloud://sounds/${id}`,
    fallbackUrl: originalUrl,
  }),

  'amazon-music': (_id: string, originalUrl: string) => ({
    deepLinkUrl: `amznm://${originalUrl.replace(/https?:\/\//, '')}`,
    fallbackUrl: originalUrl,
  }),

  'apple-podcasts': (_id: string, originalUrl: string) => ({
    deepLinkUrl: originalUrl.replace('https://', 'podcasts://'),
    fallbackUrl: originalUrl,
  }),

  walmart: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `walmart://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  etsy: (id: string, originalUrl: string) => ({
    deepLinkUrl: `etsy://listing/${id}`,
    fallbackUrl: originalUrl,
  }),

  vinted: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `vinted://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  booking: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `booking://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  // New platforms — social
  snapchat: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `snapchat://add/${id}` : 'snapchat://',
    fallbackUrl: originalUrl,
  }),

  pinterest: (id: string, originalUrl: string) => {
    if (id.startsWith('pin/')) {
      return {
        deepLinkUrl: `pinterest://pin/${id.replace('pin/', '')}`,
        fallbackUrl: originalUrl,
      };
    }
    if (id.startsWith('user/')) {
      return {
        deepLinkUrl: `pinterest://user/${id.replace('user/', '')}`,
        fallbackUrl: originalUrl,
      };
    }
    return { deepLinkUrl: 'pinterest://', fallbackUrl: originalUrl };
  },

  reddit: (id: string, originalUrl: string) => ({
    deepLinkUrl: `reddit://reddit.com/${id}`,
    fallbackUrl: originalUrl,
  }),

  weibo: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `sinaweibo://detail?mblogid=${id}` : 'sinaweibo://',
    fallbackUrl: originalUrl,
  }),

  vimeo: (id: string, originalUrl: string) => ({
    deepLinkUrl: `vimeo://app.vimeo.com/videos/${id}`,
    fallbackUrl: originalUrl,
  }),

  // Messaging
  messenger: (_id: string, originalUrl: string) => ({
    deepLinkUrl: 'fb-messenger://',
    fallbackUrl: originalUrl,
  }),

  telegram: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `tg://resolve?domain=${id}` : 'tg://',
    fallbackUrl: originalUrl,
  }),

  wechat: (_id: string, originalUrl: string) => ({
    deepLinkUrl: 'weixin://',
    fallbackUrl: originalUrl,
  }),

  qq: (_id: string, originalUrl: string) => ({
    deepLinkUrl: 'mqq://',
    fallbackUrl: originalUrl,
  }),

  // E-Commerce
  target: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `target://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  aliexpress: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `aliexpress://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  temu: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `temu://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  shopee: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `shopee://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  lazada: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `lazada://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  shein: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `shein://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  'mercado-libre': (_id: string, originalUrl: string) => ({
    deepLinkUrl: `meli://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  'best-buy': (_id: string, originalUrl: string) => ({
    deepLinkUrl: `bestbuy://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  // Music & Streaming
  pandora: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `pandora://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  iheartradio: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `ihr://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  shazam: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `shazam://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  tidal: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `tidal://${id}` : 'tidal://',
    fallbackUrl: originalUrl,
  }),

  twitch: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `twitch://stream/${id}` : 'twitch://',
    fallbackUrl: originalUrl,
  }),

  // Podcasts
  'pocket-casts': (_id: string, originalUrl: string) => ({
    deepLinkUrl: `pktc://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  overcast: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `overcast://${originalUrl.replace(/https?:\/\//, '')}`,
    fallbackUrl: originalUrl,
  }),

  castro: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `castro://${originalUrl.replace(/https?:\/\//, '')}`,
    fallbackUrl: originalUrl,
  }),

  castbox: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `castbox://${originalUrl.replace(/https?:\/\//, '')}`,
    fallbackUrl: originalUrl,
  }),

  // New Music platforms
  audiomack: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `audiomack://${id}` : 'audiomack://',
    fallbackUrl: originalUrl,
  }),

  mixcloud: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `mixcloud://mixcloud.com/${id}` : 'mixcloud://',
    fallbackUrl: originalUrl,
  }),

  tunein: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `tunein://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  beatport: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `beatport://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  // Web-only music platforms (no native URI scheme, fallback to web)
  beatstars: (_id: string, originalUrl: string) => ({
    deepLinkUrl: originalUrl,
    fallbackUrl: originalUrl,
  }),

  linkfire: (_id: string, originalUrl: string) => ({
    deepLinkUrl: originalUrl,
    fallbackUrl: originalUrl,
  }),

  // Podcasts (new)
  acast: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `acast://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  // Tour Dates
  bandsintown: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `bandsintown://artist/${id}` : 'bandsintown://',
    fallbackUrl: originalUrl,
  }),

  songkick: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `songkick://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  seated: (_id: string, originalUrl: string) => ({
    deepLinkUrl: originalUrl,
    fallbackUrl: originalUrl,
  }),

  // Video (new)
  kickstarter: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `kickstarter://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  // Social (new)
  strava: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `strava://${id}` : 'strava://',
    fallbackUrl: originalUrl,
  }),

  threads: (id: string, originalUrl: string) => {
    if (id.startsWith('user/')) {
      return {
        deepLinkUrl: `barcelona://user?username=${id.replace('user/', '')}`,
        fallbackUrl: originalUrl,
      };
    }
    if (id.startsWith('post/')) {
      return {
        deepLinkUrl: `barcelona://media?id=${id.replace('post/', '')}`,
        fallbackUrl: originalUrl,
      };
    }
    return { deepLinkUrl: 'barcelona://', fallbackUrl: originalUrl };
  },

  // Other (new)
  airbnb: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `airbnb://rooms/${id}` : 'airbnb://',
    fallbackUrl: originalUrl,
  }),

  behance: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `behance://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  calendly: (_id: string, originalUrl: string) => ({
    deepLinkUrl: originalUrl,
    fallbackUrl: originalUrl,
  }),

  gofundme: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `gofundme://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  imgur: (id: string, originalUrl: string) => ({
    deepLinkUrl: id ? `imgur://imgur.com/${id}` : 'imgur://',
    fallbackUrl: originalUrl,
  }),

  laylo: (_id: string, originalUrl: string) => ({
    deepLinkUrl: originalUrl,
    fallbackUrl: originalUrl,
  }),

  matterport: (_id: string, originalUrl: string) => ({
    deepLinkUrl: originalUrl,
    fallbackUrl: originalUrl,
  }),

  shopmy: (_id: string, originalUrl: string) => ({
    deepLinkUrl: originalUrl,
    fallbackUrl: originalUrl,
  }),

  substack: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `substack://${originalUrl.replace(/https?:\/\//, '')}`,
    fallbackUrl: originalUrl,
  }),

  typeform: (_id: string, originalUrl: string) => ({
    deepLinkUrl: originalUrl,
    fallbackUrl: originalUrl,
  }),

  // Events
  meetup: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `meetup://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  eventbrite: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `eventbrite://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),

  ticketmaster: (_id: string, originalUrl: string) => ({
    deepLinkUrl: `ticketmaster://${originalUrl.replace(/https?:\/\/(?:www\.)?/, '')}`,
    fallbackUrl: originalUrl,
  }),
};

export const generateDeeplink = (platform: Platform, originalUrl: string): DeeplinkResult => {
  // Try to extract ID from URL
  const extractor = extractors[platform];
  const id = extractor ? extractor(originalUrl) : null;

  // Generate deeplink if we have a generator for this platform
  // Bug fix: don't require id — some generators work with originalUrl only
  const generator = deeplinkGenerators[platform];
  if (generator) {
    return generator(id || '', originalUrl);
  }

  // Fallback: just use the original URL
  return {
    deepLinkUrl: originalUrl,
    fallbackUrl: originalUrl,
  };
};

// Generate a random short code for the deeplink
export const generateShortCode = (length: number = 7): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};
